import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export const useVideoUpload = () => {
  const { user, refreshProfile } = useAuthContext();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const uploadVideo = useCallback(async (file: File): Promise<string | null> => {
    if (!user) {
      toast({ title: "Not authenticated", variant: "destructive" });
      return null;
    }

    // Validate file type
    if (!file.type.startsWith("video/")) {
      toast({ title: "Invalid file type", description: "Please upload a video file", variant: "destructive" });
      return null;
    }

    // Max 50MB for videos
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({ title: "File too large", description: "Maximum video size is 50MB", variant: "destructive" });
      return null;
    }

    setUploading(true);
    setProgress({ loaded: 0, total: file.size, percentage: 0 });

    try {
      // Delete old video if exists
      const { data: existingVideos } = await supabase.storage
        .from("profile-videos")
        .list(user.id);

      if (existingVideos && existingVideos.length > 0) {
        const filesToDelete = existingVideos.map((f) => `${user.id}/${f.name}`);
        await supabase.storage.from("profile-videos").remove(filesToDelete);
      }

      // Generate unique filename
      const fileExt = file.name.split(".").pop() || "mp4";
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;

      // Upload video with progress tracking
      const { data, error } = await supabase.storage
        .from("profile-videos")
        .upload(fileName, file, {
          cacheControl: "3600",
          upsert: true,
        });

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("profile-videos")
        .getPublicUrl(fileName);

      // Update profile
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          video_url: urlData.publicUrl,
          has_intro_video: true,
        })
        .eq("id", user.id);

      if (updateError) {
        throw updateError;
      }

      setProgress({ loaded: file.size, total: file.size, percentage: 100 });
      refreshProfile();
      
      return urlData.publicUrl;
    } catch (error: any) {
      console.error("Video upload error:", error);
      toast({ 
        title: "Upload failed", 
        description: error.message || "Failed to upload video. Please try again.",
        variant: "destructive" 
      });
      return null;
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(null), 1000);
    }
  }, [user, refreshProfile]);

  const deleteVideo = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    setUploading(true);

    try {
      // List and delete all user videos
      const { data: existingVideos } = await supabase.storage
        .from("profile-videos")
        .list(user.id);

      if (existingVideos && existingVideos.length > 0) {
        const filesToDelete = existingVideos.map((f) => `${user.id}/${f.name}`);
        await supabase.storage.from("profile-videos").remove(filesToDelete);
      }

      // Update profile
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          video_url: null,
          has_intro_video: false,
          video_cover_timestamp: 0,
        })
        .eq("id", user.id);

      if (updateError) {
        throw updateError;
      }

      refreshProfile();
      return true;
    } catch (error: any) {
      console.error("Video delete error:", error);
      toast({ 
        title: "Delete failed", 
        description: error.message || "Failed to delete video",
        variant: "destructive" 
      });
      return false;
    } finally {
      setUploading(false);
    }
  }, [user, refreshProfile]);

  const updateVideoSettings = useCallback(async (settings: {
    video_show_pop_balloon?: boolean;
    video_show_global_connect?: boolean;
    video_show_exhibition?: boolean;
    video_cover_timestamp?: number;
  }): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("profiles")
        .update(settings)
        .eq("id", user.id);

      if (error) throw error;

      refreshProfile();
      return true;
    } catch (error: any) {
      console.error("Update settings error:", error);
      toast({ 
        title: "Update failed", 
        description: error.message,
        variant: "destructive" 
      });
      return false;
    }
  }, [user, refreshProfile]);

  const reportVideo = useCallback(async (
    reportedUserId: string,
    reason: string,
    details?: string
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from("video_reports")
        .insert({
          reporter_id: user.id,
          reported_user_id: reportedUserId,
          reason,
          details,
        });

      if (error) throw error;

      toast({ title: "Report submitted", description: "Thank you for helping keep our community safe." });
      return true;
    } catch (error: any) {
      console.error("Report error:", error);
      toast({ 
        title: "Report failed", 
        description: error.message,
        variant: "destructive" 
      });
      return false;
    }
  }, [user]);

  return {
    uploadVideo,
    deleteVideo,
    updateVideoSettings,
    reportVideo,
    uploading,
    progress,
  };
};